import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Table as AppkitTable } from 'appkit-react';
import '../../scss/Table.scss';
import MultiKeySearchBar from '../MultiKeySearchBar';
import SortableHeader from '../SortableHeader';
import { formatQuarterLabel } from '../utils/formatQuarterLabel';
const quarterRegex = /^Q([1-4])[ -](\d{4})$/;

const Table = ({ data, onExplainMdrm, searchKeys, onSearchKeysChange, startQuarter,
    endQuarter, }) => {
    const [showAbsChange, setShowAbsChange] = useState(false);
    const [clickedMdrm, setClickedMdrm] = useState(null);
    const [sorted, setSorted] = useState([]);
    const prevQuartersRef = useRef([]);

    const loweredKeys = (searchKeys || [])
        .map((k) => k.toLowerCase().trim())
        .filter(Boolean);

    const filteredRows = useMemo(() => {
        if (!data || data.length === 0) return [];
        if (loweredKeys.length === 0) return data;

        return data.filter((item) => {
            const row = item.data || item;
            return loweredKeys.some(
                (key) =>
                    (row.MDRM || '').toLowerCase().includes(key) ||
                    (row.Schedule || '').toLowerCase().includes(key) ||
                    (row.ItemName || '').toLowerCase().includes(key),
            );
        });
    }, [data, loweredKeys]);

    const quarters = useMemo(() => {
        const quarterSet = new Set();

        if (data && data.length > 0) {
            data.forEach((item) => {
                Object.keys(item.data || item).forEach((key) => {
                    const normalizedKey = formatQuarterLabel(key);
                    if (quarterRegex.test(key)) quarterSet.add(normalizedKey);
                });
            });
        }

        if (startQuarter) quarterSet.add(formatQuarterLabel(startQuarter));
        if (endQuarter) quarterSet.add(formatQuarterLabel(endQuarter));

        const quartersArr = Array.from(quarterSet);

        const parsed = quartersArr
            .map((q) => {
                const m = q.match(quarterRegex);
                return m ? { qStr: q, year: +m[2], qNum: +m[1] } : null;
            })
            .filter(Boolean)
            .sort((a, b) => (a.year !== b.year ? a.year - b.year : a.qNum - b.qNum));

        return parsed.map((q) => q.qStr);
    }, [data, startQuarter, endQuarter]);

    useEffect(() => {
        if (quarters.length > 0) prevQuartersRef.current = quarters;
    }, [quarters]);

    const rows = useMemo(() => {
        if (!filteredRows || filteredRows.length === 0) return [];

        if (!sorted || sorted.length === 0) {
            return filteredRows.map(itemToRow);
        }

        const { id: sortColumn, desc } = sorted[0];

        return [...filteredRows]
            .sort((a, b) => compareRows(a, b, sortColumn, desc))
            .map(itemToRow);
    }, [filteredRows, quarters, sorted]);

    function itemToRow(item) {
        const row = item.data || item;
        const quartersData = {};
        quarters.forEach((q) => {
            quartersData[q] = row[q] ?? '';
        });
        return {
            mdrm: row.MDRM || '',
            schedule: row.Schedule || '',
            lineItemCode: row.LineItemCode || '',
            itemName: row.ItemName || '',
            change: row.Change || row.change || '',
            changeAbs: row.ChangeAbs || '',
            ...quartersData,
        };
    }

    function compareRows(a, b, sortColumn, desc) {
        const aVal = (a.data || a)[sortColumn];
        const bVal = (b.data || b)[sortColumn];

        if (aVal === bVal) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;

        if (typeof aVal === 'number' && typeof bVal === 'number') {
            return desc ? bVal - aVal : aVal - bVal;
        }

        const aStr = aVal.toString().toLowerCase();
        const bStr = bVal.toString().toLowerCase();

        if (aStr < bStr) return desc ? 1 : -1;
        if (aStr > bStr) return desc ? -1 : 1;
        return 0;
    }

    const onExplainClick = (mdrm) => {
        setClickedMdrm(mdrm);
        onExplainMdrm?.(mdrm);
        setTimeout(() => setClickedMdrm(null), 800);
    };

    const onSortedChange = (newSorted, column) => {
        setSorted(newSorted);
    };

    const columns = useMemo(() => {
        const baseCols = [
            {
                Header: '✨',
                id: 'explain',
                width: 30,
                sortable: false,
                Cell: ({ row }) => {
                    const mdrm = row._original?.mdrm || '';
                    return (
                        <div
                            title="Explain MDRM"
                            onClick={() => onExplainClick(mdrm)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') onExplainClick(mdrm);
                            }}
                            tabIndex={0}
                            role="button"
                            aria-label={`Explain ${mdrm}`}
                        >
                            {'✨'}
                        </div>
                    );
                },
            },
            {
                id: 'mdrm',
                accessor: 'mdrm',
                sortable: false,
                width: 70,
                Header: ({ column }) => (
                    <SortableHeader
                        column={column}
                        label="MDRM"
                        sorted={sorted}
                        onSortedChange={onSortedChange}
                    />
                ),
            },
            {
                id: 'schedule',
                accessor: 'schedule',
                sortable: false,
                className: 'schedule-cell',
                width: 80,
                Header: ({ column }) => (
                    <SortableHeader
                        column={column}
                        label="Schedule"
                        sorted={sorted}
                        onSortedChange={onSortedChange}
                    />
                ),
            },
            {
                id: 'lineItemCode',
                accessor: 'lineItemCode',
                sortable: false,
                width: 55,
                Header: ({ column }) => (
                    <SortableHeader
                        column={column}
                        label={
                            <div className="multiline-headers">
                                Line
                                <br />
                                Item
                                <br />
                                Code
                            </div>
                        }
                        sorted={sorted}
                        onSortedChange={onSortedChange}
                    />
                ),
                Cell: ({ value }) => {
                    if (!value) return null;

                    const maxLength = 5;
                    const truncated = value.length > maxLength ? value.slice(0, maxLength) + '...' : value;

                    return (
                        <div className="markdown-cell" title={value}>
                            {truncated}
                        </div>
                    );
                },
            },
            {
                id: 'itemName',
                accessor: 'itemName',
                sortable: false,
                width: '90%',
                Header: ({ column }) => (
                    <SortableHeader
                        column={column}
                        label="Item Name"
                        sorted={sorted}
                        onSortedChange={onSortedChange}
                    />
                ),
                Cell: ({ value }) => {
                    if (!value) return null;

                    return (
                        <div className="item-name-cell" title={value}>
                            {value}
                        </div>
                    );
                },
            },

            ...quarters.map((q) => ({
                id: q,
                accessor: q,
                sortable: false,
                width: 100,
                Header: ({ column }) => (
                    <SortableHeader
                        column={column}
                        label={q}
                        sorted={sorted}
                        onSortedChange={onSortedChange}
                    />
                ),
                Cell: ({ value }) => (value !== '' ? Number(value).toLocaleString() : ''),
            })),
            {
                id: 'change',
                accessor: 'change',
                sortable: false,
                width: 70,
                Header: ({ column }) => (
                    <SortableHeader
                        column={column}
                        label={
                            <div className="multiline-headers">
                                Change
                                <br />
                                (%)
                            </div>
                        }
                        sorted={sorted}
                        onSortedChange={onSortedChange}
                    />
                ),
                Cell: ({ value }) => {
                    const num = parseFloat((value || '').toString().replace('%', ''));
                    const className =
                        !Number.isNaN(num) && num !== 0
                            ? num > 0
                                ? 'change-positive'
                                : 'change-negative'
                            : '';
                    return <div className={className}>{value}</div>;
                },
            },
        ];
        if (showAbsChange) {
            baseCols.push({
                id: 'changeAbs',
                accessor: 'changeAbs',
                sortable: false,
                width: 70,
                Header: ({ column }) => (
                    <SortableHeader
                        column={column}
                        label={
                            <div className="multiline-headers">
                                Abs
                                <br />
                                Change
                                <br />
                                (%)
                            </div>
                        }
                        sorted={sorted}
                        onSortedChange={onSortedChange}
                    />
                ),
            });
        }
        return baseCols;
    }, [quarters, showAbsChange, sorted]);

    return (
        <div className="table-demo">
            <div className="table-demo-wrapper">
                <div className="table-header">
                    <label>
                        <input
                            type="checkbox"
                            checked={showAbsChange}
                            onChange={() => setShowAbsChange((s) => !s)}
                            aria-label="Toggle showing absolute change percentage column"
                        />
                        Show Abs Change (%)
                    </label>
                    <div className="searchbar-container">
                        <MultiKeySearchBar
                            onKeysChange={onSearchKeysChange}
                            placeholder="Search MDRM, Schedule, Item Name..."
                        />
                    </div>
                </div>

                <AppkitTable
                    data={rows}
                    columns={columns}
                    sortable={false}
                    sorted={sorted}
                    onSortedChange={onSortedChange}
                    defaultPageSize={10}
                    showPageSizeOptions={false}
                    size="sm"
                />
            </div>
        </div>
    );
};

export default Table;
